// pages/DataManagement.js - Updated to exclude Cash Bills from CA Export

import { useState, useEffect } from "react";
import { database } from "../firebase/firebase";
import { ref, get, set, remove, push, onValue } from "firebase/database";
import { useNavigate } from "react-router-dom";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import "../styles/main.css";

export default function DataManagement() {
  const navigate = useNavigate();
  
  // State for backup
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // State for financial year backup
  const [showBackupFYModal, setShowBackupFYModal] = useState(false);
  const [selectedFYForBackup, setSelectedFYForBackup] = useState("");
  const [backupFYLoading, setBackupFYLoading] = useState(false);
  
  // State for financial year deletion
  const [showDeleteFYModal, setShowDeleteFYModal] = useState(false);
  const [selectedFYToDelete, setSelectedFYToDelete] = useState("");
  const [financialYearData, setFinancialYearData] = useState({});
  const [deleteFYLoading, setDeleteFYLoading] = useState(false);
  
  // State for data statistics
  const [stats, setStats] = useState({
    items: 0,
    customers: 0,
    invoices: 0,
    purchases: 0,
    rawBills: 0,
    payments: 0,
    totalSales: 0,
    totalPurchases: 0
  });
  
  // State for CA Export (Excludes Cash Bills by default)
  const [showCAExport, setShowCAExport] = useState(false);
  const [selectedBills, setSelectedBills] = useState([]);
  const [financialYear, setFinancialYear] = useState("");
  const [availableYears, setAvailableYears] = useState([]);
  const [billList, setBillList] = useState([]);
  const [filteredBills, setFilteredBills] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [exportFormat, setExportFormat] = useState("excel"); // excel or zip
  
  // Company details
  const [companyDetails, setCompanyDetails] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
    gstin: "",
    state: "",
    stateCode: "",
    pan: ""
  });
  
  // Settings
  const [settings, setSettings] = useState({ financialYear: "2025-26" });
  
  // Load data statistics
  useEffect(() => {
    loadStats();
    loadCompanyDetails();
    loadSettings();
    loadFinancialYears();
  }, []);
  
  const loadStats = async () => {
    try {
      const itemsRef = ref(database, "items");
      const itemsSnapshot = await get(itemsRef);
      const itemsCount = itemsSnapshot.exists() ? Object.keys(itemsSnapshot.val()).length : 0;
      
      const customersRef = ref(database, "customers");
      const customersSnapshot = await get(customersRef);
      const customersCount = customersSnapshot.exists() ? Object.keys(customersSnapshot.val()).length : 0;
      
      const financialYearsRef = ref(database, "financialYears");
      const financialYearsSnapshot = await get(financialYearsRef);
      
      let invoicesCount = 0;
      let purchasesCount = 0;
      let rawBillsCount = 0;
      let paymentsCount = 0;
      let totalSales = 0;
      let totalPurchases = 0;
      const fyData = {};
      
      if (financialYearsSnapshot.exists()) {
        const years = financialYearsSnapshot.val();
        for (const [year, yearData] of Object.entries(years)) {
          fyData[year] = {
            invoices: yearData.bills ? Object.keys(yearData.bills).length : 0,
            purchases: yearData.purchases ? Object.keys(yearData.purchases).length : 0,
            rawBills: yearData.rawBills ? Object.keys(yearData.rawBills).length : 0,
            payments: yearData.payments ? Object.keys(yearData.payments).length : 0
          };
          
          if (yearData.bills) {
            invoicesCount += Object.keys(yearData.bills).length;
            Object.values(yearData.bills).forEach(bill => {
              totalSales += bill.grandTotal || 0;
            });
          }
          if (yearData.purchases) {
            purchasesCount += Object.keys(yearData.purchases).length;
            Object.values(yearData.purchases).forEach(purchase => {
              totalPurchases += purchase.grandTotal || 0;
            });
          }
          if (yearData.rawBills) {
            rawBillsCount += Object.keys(yearData.rawBills).length;
          }
          if (yearData.payments) {
            paymentsCount += Object.keys(yearData.payments).length;
          }
        }
      }
      
      setFinancialYearData(fyData);
      setStats({
        items: itemsCount,
        customers: customersCount,
        invoices: invoicesCount,
        purchases: purchasesCount,
        rawBills: rawBillsCount,
        payments: paymentsCount,
        totalSales: totalSales,
        totalPurchases: totalPurchases
      });
      
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };
  
  const loadCompanyDetails = async () => {
    const companyRef = ref(database, "company");
    const snapshot = await get(companyRef);
    if (snapshot.exists()) {
      setCompanyDetails(snapshot.val());
    }
  };
  
  const loadSettings = async () => {
    const settingsRef = ref(database, "settings");
    const snapshot = await get(settingsRef);
    if (snapshot.exists()) {
      setSettings(snapshot.val());
      setFinancialYear(snapshot.val().financialYear || "2025-26");
    }
  };
  
  const loadFinancialYears = async () => {
    const financialYearsRef = ref(database, "financialYears");
    const snapshot = await get(financialYearsRef);
    if (snapshot.exists()) {
      const years = Object.keys(snapshot.val());
      setAvailableYears(years.sort().reverse());
    }
  };
  
  // BACKUP ALL DATA
  const handleBackupAll = async () => {
    setBackupLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const zip = new JSZip();
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      
      const dbRef = ref(database);
      const snapshot = await get(dbRef);
      const allData = snapshot.val();
      
      if (!allData) {
        throw new Error("No data to backup");
      }
      
      const backupMetadata = {
        backupDate: new Date().toISOString(),
        version: "1.0",
        appName: "IndianTrade Billing",
        companyName: companyDetails.name,
        financialYear: settings.financialYear,
        stats: stats,
        backupType: "full"
      };
      
      zip.file("metadata.json", JSON.stringify(backupMetadata, null, 2));
      zip.file("complete_backup.json", JSON.stringify(allData, null, 2));
      
      if (allData.items) zip.file("items.json", JSON.stringify(allData.items, null, 2));
      if (allData.customers) zip.file("customers.json", JSON.stringify(allData.customers, null, 2));
      if (allData.settings) zip.file("settings.json", JSON.stringify(allData.settings, null, 2));
      if (allData.company) zip.file("company.json", JSON.stringify(allData.company, null, 2));
      if (allData.financialYears) zip.file("financialYears.json", JSON.stringify(allData.financialYears, null, 2));
      if (allData.stockHistory) zip.file("stockHistory.json", JSON.stringify(allData.stockHistory, null, 2));
      
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, `indiantrade_full_backup_${timestamp}.zip`);
      
      setSuccess(`Full backup created successfully!`);
      
    } catch (error) {
      console.error("Backup error:", error);
      setError("Failed to create backup: " + error.message);
    } finally {
      setBackupLoading(false);
    }
  };
  
  // BACKUP SPECIFIC FINANCIAL YEAR
  const handleBackupFinancialYear = async () => {
    if (!selectedFYForBackup) {
      alert("Please select a financial year to backup");
      return;
    }
    
    setBackupFYLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const zip = new JSZip();
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      
      const dbRef = ref(database);
      const snapshot = await get(dbRef);
      const allData = snapshot.val();
      
      if (!allData) {
        throw new Error("No data to backup");
      }
      
      const backupData = {
        backupDate: new Date().toISOString(),
        version: "1.0",
        appName: "IndianTrade Billing",
        companyName: companyDetails.name,
        financialYear: selectedFYForBackup,
        backupType: "financial_year",
        stats: {
          items: stats.items,
          customers: stats.customers,
          ...financialYearData[selectedFYForBackup]
        }
      };
      
      if (allData.items) backupData.items = allData.items;
      if (allData.customers) backupData.customers = allData.customers;
      if (allData.company) backupData.company = allData.company;
      if (allData.settings) backupData.settings = allData.settings;
      
      if (allData.financialYears && allData.financialYears[selectedFYForBackup]) {
        backupData.financialYears = {
          [selectedFYForBackup]: allData.financialYears[selectedFYForBackup]
        };
      }
      
      zip.file("metadata.json", JSON.stringify(backupData, null, 2));
      zip.file(`financial_year_${selectedFYForBackup}_backup.json`, JSON.stringify(backupData, null, 2));
      
      if (allData.items) zip.file("items.json", JSON.stringify(allData.items, null, 2));
      if (allData.customers) zip.file("customers.json", JSON.stringify(allData.customers, null, 2));
      if (allData.company) zip.file("company.json", JSON.stringify(allData.company, null, 2));
      if (allData.settings) zip.file("settings.json", JSON.stringify(allData.settings, null, 2));
      
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, `indiantrade_backup_${selectedFYForBackup}_${timestamp}.zip`);
      
      setSuccess(`Backup for financial year ${selectedFYForBackup} created successfully!`);
      setShowBackupFYModal(false);
      setSelectedFYForBackup("");
      
    } catch (error) {
      console.error("Backup error:", error);
      setError("Failed to create backup: " + error.message);
    } finally {
      setBackupFYLoading(false);
    }
  };
  
  // RESTORE FUNCTION
  const handleRestore = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!window.confirm("WARNING: Restoring will overwrite ALL existing data! This action cannot be undone. Are you sure?")) {
      return;
    }
    
    setRestoreLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const zip = new JSZip();
      const contents = await zip.loadAsync(file);
      
      const backupFile = contents.file("complete_backup.json") || contents.file(/.*_backup\.json/);
      if (!backupFile) {
        throw new Error("Invalid backup file: backup.json not found");
      }
      
      const backupText = await backupFile.async("text");
      const backupData = JSON.parse(backupText);
      
      if (!backupData || typeof backupData !== 'object') {
        throw new Error("Invalid backup data format");
      }
      
      const restorePromises = [];
      
      for (const [key, value] of Object.entries(backupData)) {
        if (value && typeof value === 'object' && key !== 'backupDate' && key !== 'version' && key !== 'appName' && key !== 'backupType') {
          const dataRef = ref(database, key);
          restorePromises.push(set(dataRef, value));
        }
      }
      
      await Promise.all(restorePromises);
      
      setSuccess("Data restored successfully! Please refresh the page.");
      
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      
    } catch (error) {
      console.error("Restore error:", error);
      setError("Failed to restore backup: " + error.message);
    } finally {
      setRestoreLoading(false);
      event.target.value = "";
    }
  };
  
  // Delete specific financial year
  const handleDeleteFinancialYear = async () => {
    if (!selectedFYToDelete) {
      alert("Please select a financial year to delete");
      return;
    }
    
    const confirmMessage = `⚠️ WARNING: This will DELETE ALL data for financial year ${selectedFYToDelete} including invoices, purchases, and payments! This action CANNOT be undone! ⚠️\n\nType 'DELETE ${selectedFYToDelete}' to confirm:`;
    const confirmation = prompt(confirmMessage);
    
    if (confirmation !== `DELETE ${selectedFYToDelete}`) {
      alert("Deletion cancelled.");
      return;
    }
    
    setDeleteFYLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const financialYearRef = ref(database, `financialYears/${selectedFYToDelete}`);
      await remove(financialYearRef);
      
      setSuccess(`Financial year ${selectedFYToDelete} has been deleted successfully!`);
      setShowDeleteFYModal(false);
      setSelectedFYToDelete("");
      
      await loadStats();
      await loadFinancialYears();
      
    } catch (error) {
      console.error("Delete financial year error:", error);
      setError("Failed to delete financial year: " + error.message);
    } finally {
      setDeleteFYLoading(false);
    }
  };
  
  // Load bills for CA export - EXCLUDES CASH BILLS
  const loadBillsForExport = async () => {
    setExportLoading(true);
    try {
      const allBills = [];
      const financialYearsRef = ref(database, "financialYears");
      const snapshot = await get(financialYearsRef);
      
      if (snapshot.exists()) {
        const years = snapshot.val();
        for (const [year, yearData] of Object.entries(years)) {
          // Load Tax Invoices only - EXCLUDING CASH BILLS
          if (yearData.bills) {
            Object.entries(yearData.bills).forEach(([id, bill]) => {
              allBills.push({
                id,
                ...bill,
                type: "tax-invoice",
                typeLabel: "Tax Invoice",
                year: year
              });
            });
          }
          // Load Purchases only - EXCLUDING CASH BILLS
          if (yearData.purchases) {
            Object.entries(yearData.purchases).forEach(([id, purchase]) => {
              allBills.push({
                id,
                ...purchase,
                type: "purchase",
                typeLabel: "Purchase Order",
                year: year
              });
            });
          }
          // RAW BILLS (CASH BILLS) ARE EXCLUDED - NOT SENT TO CA
        }
      }
      
      allBills.sort((a, b) => new Date(b.date) - new Date(a.date));
      setBillList(allBills);
      setFilteredBills(allBills);
      
    } catch (error) {
      console.error("Error loading bills:", error);
      setError("Failed to load bills for export");
    } finally {
      setExportLoading(false);
    }
  };
  
  // Filter bills for CA export
  useEffect(() => {
    let filtered = [...billList];
    
    if (searchTerm) {
      filtered = filtered.filter(bill => 
        (bill.invoiceNo || bill.purchaseNo)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (bill.customerName || bill.supplierName)?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filterType !== "all") {
      filtered = filtered.filter(bill => bill.type === filterType);
    }
    
    if (financialYear && financialYear !== "all") {
      filtered = filtered.filter(bill => bill.year === financialYear);
    }
    
    setFilteredBills(filtered);
  }, [billList, searchTerm, filterType, financialYear]);
  
  // Generate Excel file for CA export
  const generateExcelForCA = (bills) => {
    const workbook = XLSX.utils.book_new();
    
    // Sales Register (Tax Invoices only)
    const salesData = bills
      .filter(b => b.type === "tax-invoice")
      .map(bill => ({
        "Invoice No": bill.invoiceNo,
        "Date": bill.date,
        "Customer Name": bill.customerName,
        "Customer GSTIN": bill.customerGST || "N/A",
        "Customer State": bill.customerState || "N/A",
        "Taxable Value": bill.taxableValue || 0,
        "CGST": bill.cgst || 0,
        "SGST": bill.sgst || 0,
        "IGST": bill.igst || 0,
        "Total GST": bill.totalGST || 0,
        "Total Discount": bill.totalDiscount || 0,
        "Grand Total": bill.grandTotal || 0,
        "Round Off": bill.roundOffAmount || 0,
        "Net Total": bill.roundedGrandTotal || bill.grandTotal || 0,
        "Payment Status": bill.status || "Completed"
      }));
    
    const salesSheet = XLSX.utils.json_to_sheet(salesData);
    XLSX.utils.book_append_sheet(workbook, salesSheet, "Sales Register");
    
    // Purchase Register
    const purchaseData = bills
      .filter(b => b.type === "purchase")
      .map(purchase => ({
        "Purchase No": purchase.purchaseNo,
        "Date": purchase.date,
        "Supplier Name": purchase.supplierName,
        "Supplier GSTIN": purchase.supplierGST || "N/A",
        "Supplier State": purchase.supplierState || "N/A",
        "Taxable Value": purchase.taxableValue || 0,
        "CGST": purchase.cgst || 0,
        "SGST": purchase.sgst || 0,
        "IGST": purchase.igst || 0,
        "Total GST": purchase.totalGST || 0,
        "Total Discount": purchase.totalDiscount || 0,
        "Grand Total": purchase.grandTotal || 0,
        "Supplier Invoice No": purchase.supplierInvoiceNo || "",
        "Supplier Invoice Date": purchase.supplierInvoiceDate || "",
        "Status": purchase.status || "Completed"
      }));
    
    const purchaseSheet = XLSX.utils.json_to_sheet(purchaseData);
    XLSX.utils.book_append_sheet(workbook, purchaseSheet, "Purchase Register");
    
    // Item-wise Sales (Tax Invoices only)
    const itemWiseSales = [];
    bills.forEach(bill => {
      if (bill.items && bill.type === "tax-invoice") {
        bill.items.forEach(item => {
          itemWiseSales.push({
            "Invoice No": bill.invoiceNo,
            "Invoice Date": bill.date,
            "Customer": bill.customerName,
            "Item Name": item.name,
            "HSN": item.hsn || "",
            "Quantity": item.qty,
            "Unit": item.unit || "NOS",
            "Rate": item.price,
            "Discount %": item.discount || 0,
            "Taxable Value": item.taxableValue,
            "GST %": item.gst || 0,
            "CGST": item.gstDetails?.cgst || 0,
            "SGST": item.gstDetails?.sgst || 0,
            "IGST": item.gstDetails?.igst || 0,
            "Total GST": item.gstDetails?.total || 0,
            "Net Total": item.netTotal
          });
        });
      }
    });
    
    const itemSheet = XLSX.utils.json_to_sheet(itemWiseSales);
    XLSX.utils.book_append_sheet(workbook, itemSheet, "Item-wise Sales");
    
    // GST Summary
    const gstSummary = {};
    bills.forEach(bill => {
      if (bill.items) {
        bill.items.forEach(item => {
          const rate = item.gst || 0;
          if (!gstSummary[rate]) {
            gstSummary[rate] = {
              "GST Rate": `${rate}%`,
              "Taxable Value": 0,
              "CGST": 0,
              "SGST": 0,
              "IGST": 0,
              "Total GST": 0
            };
          }
          gstSummary[rate]["Taxable Value"] += item.taxableValue || 0;
          gstSummary[rate]["CGST"] += item.gstDetails?.cgst || 0;
          gstSummary[rate]["SGST"] += item.gstDetails?.sgst || 0;
          gstSummary[rate]["IGST"] += item.gstDetails?.igst || 0;
          gstSummary[rate]["Total GST"] += item.gstDetails?.total || 0;
        });
      }
    });
    
    const gstSheet = XLSX.utils.json_to_sheet(Object.values(gstSummary));
    XLSX.utils.book_append_sheet(workbook, gstSheet, "GST Summary");
    
    // Party-wise Summary
    const partySummary = {};
    bills.forEach(bill => {
      const partyName = bill.customerName || bill.supplierName;
      const partyGST = bill.customerGST || bill.supplierGST || "N/A";
      const key = `${partyName}_${partyGST}`;
      
      if (!partySummary[key]) {
        partySummary[key] = {
          "Party Name": partyName,
          "GSTIN": partyGST,
          "Sales Amount": 0,
          "Purchase Amount": 0,
          "Taxable Value": 0,
          "Total GST": 0,
          "Net Amount": 0
        };
      }
      
      if (bill.type === "purchase") {
        partySummary[key]["Purchase Amount"] += bill.grandTotal || 0;
      } else {
        partySummary[key]["Sales Amount"] += bill.grandTotal || 0;
      }
      partySummary[key]["Taxable Value"] += bill.taxableValue || 0;
      partySummary[key]["Total GST"] += bill.totalGST || 0;
      partySummary[key]["Net Amount"] += bill.grandTotal || 0;
    });
    
    const partySheet = XLSX.utils.json_to_sheet(Object.values(partySummary));
    XLSX.utils.book_append_sheet(workbook, partySheet, "Party-wise Summary");
    
    return workbook;
  };
  
  // Generate individual bill PDF (HTML format)
  const generateBillPDF = (bill) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${bill.invoiceNo || bill.purchaseNo}</title>
        <meta charset="UTF-8">
        <style>
          body { font-family: 'Segoe UI', sans-serif; padding: 40px; margin: 0; background: white; }
          .invoice-container { max-width: 1000px; margin: 0 auto; border: 1px solid #ddd; padding: 30px; }
          .header { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #333; }
          .company-name { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
          .company-address { font-size: 12px; color: #666; }
          .bill-title { font-size: 20px; font-weight: bold; text-align: center; margin: 20px 0; }
          .bill-info { display: flex; justify-content: space-between; margin-bottom: 30px; padding: 15px; background: #f9f9f9; }
          .party-details { margin-bottom: 30px; padding: 15px; background: #f5f5f5; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
          th { background: #f0f0f0; }
          .amount { text-align: right; }
          .totals { margin-top: 20px; text-align: right; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; font-size: 11px; }
        </style>
      </head>
      <body>
        <div class="invoice-container">
          <div class="header">
            <div class="company-name">${companyDetails.name || "Your Company"}</div>
            <div class="company-address">${companyDetails.address || ""}</div>
            <div>GSTIN: ${companyDetails.gstin || "N/A"} | PAN: ${companyDetails.pan || "N/A"}</div>
          </div>
          
          <div class="bill-title">${bill.typeLabel}</div>
          
          <div class="bill-info">
            <div>
              <strong>${bill.type === "purchase" ? "Purchase No" : "Invoice No"}:</strong> ${bill.invoiceNo || bill.purchaseNo}<br>
              <strong>Date:</strong> ${bill.date}<br>
              <strong>Financial Year:</strong> ${bill.year || settings.financialYear}
            </div>
            <div>
              <strong>Status:</strong> ${bill.status || "Completed"}
            </div>
          </div>
          
          <div class="party-details">
            <strong>${bill.type === "purchase" ? "Supplier Details" : "Customer Details"}:</strong><br>
            <strong>${bill.customerName || bill.supplierName}</strong><br>
            ${bill.customerGST || bill.supplierGST ? `GSTIN: ${bill.customerGST || bill.supplierGST}<br>` : ""}
            ${bill.customerAddress || bill.supplierAddress ? `📍 ${bill.customerAddress || bill.supplierAddress}` : ""}
          </div>
          
          <h3>Items</h3>
          <table>
            <thead>
              <tr><th>Item Name</th><th>HSN</th><th>Qty</th><th>Rate</th><th>Taxable Value</th><th>GST %</th><th>GST Amount</th><th>Net Total</th> </thead>
            <tbody>
              ${(bill.items || []).map(item => `
                <tr>
                  <td>${item.name} \n<td class="amount">${item.hsn || "-"} \n<td class="amount">${item.qty} \n<td class="amount">${item.price?.toFixed(2) || 0} \n<td class="amount">${item.taxableValue?.toFixed(2) || 0} \n<td class="amount">${item.gst || 0}% \n<td class="amount">${item.gstDetails?.total?.toFixed(2) || 0} \n<td class="amount">${item.netTotal?.toFixed(2) || 0}
              `).join("")}
            </tbody>
          </table>
          
          <div class="totals">
            <p><strong>Taxable Value:</strong> ₹${(bill.taxableValue || 0).toFixed(2)}</p>
            ${bill.cgst > 0 ? `<p><strong>CGST:</strong> ₹${(bill.cgst || 0).toFixed(2)}</p>` : ""}
            ${bill.sgst > 0 ? `<p><strong>SGST:</strong> ₹${(bill.sgst || 0).toFixed(2)}</p>` : ""}
            ${bill.igst > 0 ? `<p><strong>IGST:</strong> ₹${(bill.igst || 0).toFixed(2)}</p>` : ""}
            <p><strong>Grand Total:</strong> ₹${(bill.grandTotal || 0).toFixed(2)}</p>
          </div>
          
          <div class="footer">
            <div>${companyDetails.terms || "Thank you for your business!"}</div>
            <div>Generated on: ${new Date().toLocaleString()}</div>
          </div>
        </div>
      </body>
      </html>
    `;
  };
  
  // Calculate GST Summary
  const calculateGSTSummary = (bills) => {
    const summary = {
      totalTaxableValue: 0,
      totalCGST: 0,
      totalSGST: 0,
      totalIGST: 0,
      totalGST: 0,
      totalAmount: 0,
      byRate: {}
    };
    
    bills.forEach(bill => {
      summary.totalTaxableValue += bill.taxableValue || 0;
      summary.totalCGST += bill.cgst || 0;
      summary.totalSGST += bill.sgst || 0;
      summary.totalIGST += bill.igst || 0;
      summary.totalGST += bill.totalGST || 0;
      summary.totalAmount += bill.grandTotal || 0;
      
      if (bill.items) {
        bill.items.forEach(item => {
          const rate = item.gst || 0;
          if (!summary.byRate[rate]) {
            summary.byRate[rate] = { rate, taxableValue: 0, cgst: 0, sgst: 0, igst: 0, totalGST: 0 };
          }
          summary.byRate[rate].taxableValue += item.taxableValue || 0;
          summary.byRate[rate].cgst += item.gstDetails?.cgst || 0;
          summary.byRate[rate].sgst += item.gstDetails?.sgst || 0;
          summary.byRate[rate].igst += item.gstDetails?.igst || 0;
          summary.byRate[rate].totalGST += item.gstDetails?.total || 0;
        });
      }
    });
    
    return summary;
  };
  
  // Generate GST Report HTML
  const generateGSTReport = (summary, bills) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>GST Summary Report</title>
        <style>
          body { font-family: 'Segoe UI', sans-serif; padding: 40px; }
          .header { text-align: center; margin-bottom: 30px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
          th { background: #f0f0f0; }
          .amount { text-align: right; }
          .summary-box { background: #f9f9f9; padding: 20px; margin: 20px 0; border-radius: 8px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-name">${companyDetails.name || "Your Company"}</div>
          <div>GST Summary Report</div>
          <div>Total Bills: ${bills.length}</div>
          <div>GSTIN: ${companyDetails.gstin || "N/A"}</div>
        </div>
        
        <div class="summary-box">
          <h3>GST Summary</h3>
          <table>
            <tr><td>Total Taxable Value:</td><td class="amount">₹${summary.totalTaxableValue.toFixed(2)}</td> </tr>
            <tr><td>Total CGST:</td><td class="amount">₹${summary.totalCGST.toFixed(2)}</td></tr>
            <tr><td>Total SGST:</td><td class="amount">₹${summary.totalSGST.toFixed(2)}</td></tr>
            <tr><td>Total IGST:</td><td class="amount">₹${summary.totalIGST.toFixed(2)}</td></tr>
            <tr><td><strong>Total GST:</strong></td><td class="amount"><strong>₹${summary.totalGST.toFixed(2)}</strong></td></tr>
            <tr><td><strong>Grand Total:</strong></td><td class="amount"><strong>₹${summary.totalAmount.toFixed(2)}</strong></td></tr>
           </table>
        </div>
        
        <h3>GST Breakup by Rate</h3>
        <table>
          <thead><tr><th>GST Rate</th><th>Taxable Value</th><th>CGST</th><th>SGST</th><th>IGST</th><th>Total GST</th></tr></thead>
          <tbody>
            ${Object.values(summary.byRate).sort((a,b) => a.rate - b.rate).map(rate => `
              <tr>
                <td>${rate.rate}%</td>
                <td class="amount">₹${rate.taxableValue.toFixed(2)}</td>
                <td class="amount">₹${rate.cgst.toFixed(2)}</td>
                <td class="amount">₹${rate.sgst.toFixed(2)}</td>
                <td class="amount">₹${rate.igst.toFixed(2)}</td>
                <td class="amount">₹${rate.totalGST.toFixed(2)}</td>
              </tr>
            `).join("")}
          </tbody>
        </table>
        
        <div class="footer">Generated on: ${new Date().toLocaleString()}</div>
      </body>
      </html>
    `;
  };
  
  // CA Export Handler
  const handleCAExport = async () => {
    if (selectedBills.length === 0) {
      alert("Please select at least one bill to export");
      return;
    }
    
    setExportLoading(true);
    
    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `CA_Export_${companyDetails.name || "Company"}_${timestamp}`;
      
      if (exportFormat === "excel") {
        const workbook = generateExcelForCA(selectedBills);
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `${fileName}.xlsx`);
      } else {
        const zip = new JSZip();
        const caFolder = zip.folder(fileName);
        
        const workbook = generateExcelForCA(selectedBills);
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        caFolder.file("CA_Report.xlsx", excelBuffer);
        
        const gstSummary = calculateGSTSummary(selectedBills);
        const gstReport = generateGSTReport(gstSummary, selectedBills);
        caFolder.file("GST_Report.html", gstReport);
        
        for (const bill of selectedBills) {
          const pdfContent = generateBillPDF(bill);
          const billNo = bill.invoiceNo || bill.purchaseNo;
          caFolder.file(`${bill.typeLabel}_${billNo}.html`, pdfContent);
        }
        
        const content = await zip.generateAsync({ type: "blob" });
        saveAs(content, `${fileName}.zip`);
      }
      
      setSuccess(`Exported ${selectedBills.length} bills successfully!`);
      setShowCAExport(false);
      setSelectedBills([]);
      
    } catch (error) {
      console.error("CA Export error:", error);
      setError("Failed to export bills: " + error.message);
    } finally {
      setExportLoading(false);
    }
  };
  
  // Toggle bill selection
  const toggleBillSelection = (bill) => {
    const exists = selectedBills.find(b => b.id === bill.id);
    if (exists) {
      setSelectedBills(selectedBills.filter(b => b.id !== bill.id));
    } else {
      setSelectedBills([...selectedBills, bill]);
    }
  };
  
  // Select all bills
  const selectAllBills = () => {
    if (selectedBills.length === filteredBills.length) {
      setSelectedBills([]);
    } else {
      setSelectedBills([...filteredBills]);
    }
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2
    }).format(amount || 0);
  };
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN");
  };
  
  return (
    <div className="data-management-page">
      {/* Header */}
      <div className="page-header">
        <h2>📊 Data Management</h2>
        <div className="header-actions">
          <button onClick={() => navigate("/")} className="back-btn">
            🏠 Home
          </button>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card"><div className="stat-icon">📦</div><div className="stat-info"><div className="stat-label">Items</div><div className="stat-value">{stats.items}</div></div></div>
        <div className="stat-card"><div className="stat-icon">👥</div><div className="stat-info"><div className="stat-label">Customers</div><div className="stat-value">{stats.customers}</div></div></div>
        <div className="stat-card"><div className="stat-icon">🧾</div><div className="stat-info"><div className="stat-label">Tax Invoices</div><div className="stat-value">{stats.invoices}</div></div></div>
        <div className="stat-card"><div className="stat-icon">📥</div><div className="stat-info"><div className="stat-label">Purchases</div><div className="stat-value">{stats.purchases}</div></div></div>
        <div className="stat-card"><div className="stat-icon">💵</div><div className="stat-info"><div className="stat-label">Cash Bills</div><div className="stat-value">{stats.rawBills}</div></div></div>
        <div className="stat-card"><div className="stat-icon">💰</div><div className="stat-info"><div className="stat-label">Total Sales</div><div className="stat-value">{formatCurrency(stats.totalSales)}</div></div></div>
      </div>
      
      {/* Messages */}
      {error && <div className="error-alert"><span>⚠️</span><p>{error}</p><button onClick={() => setError(null)}>✕</button></div>}
      {success && <div className="success-alert"><span>✅</span><p>{success}</p><button onClick={() => setSuccess(null)}>✕</button></div>}
      
      {/* Backup Section */}
      <div className="section-card">
        <h3>💾 Backup Data</h3>
        <div className="backup-buttons">
          <button className="backup-btn" onClick={handleBackupAll} disabled={backupLoading}>
            {backupLoading ? <><span className="spinner"></span>Creating...</> : "📥 Backup All Data"}
          </button>
          <button className="backup-fy-btn" onClick={() => setShowBackupFYModal(true)} disabled={backupLoading}>
            📅 Backup Financial Year
          </button>
        </div>
      </div>
      
      {/* Restore Section */}
      <div className="section-card">
        <h3>🔄 Restore Data</h3>
        <p>Restore data from a previous backup file. This will OVERWRITE all existing data!</p>
        <input type="file" accept=".zip" onChange={handleRestore} disabled={restoreLoading} id="restore-file" style={{ display: "none" }} />
        <button className="restore-btn" onClick={() => document.getElementById("restore-file").click()} disabled={restoreLoading}>
          {restoreLoading ? <><span className="spinner"></span>Restoring...</> : "📂 Upload Backup & Restore"}
        </button>
      </div>
      
      {/* Financial Year Breakdown */}
      <div className="section-card">
        <h3>📅 Financial Year Breakdown</h3>
        <div className="financial-years-grid">
          {Object.entries(financialYearData).map(([year, data]) => (
            <div key={year} className="financial-year-card">
              <div className="fy-header">
                <strong>{year}</strong>
                <button className="delete-fy-btn" onClick={() => { setSelectedFYToDelete(year); setShowDeleteFYModal(true); }}>🗑️ Delete Year</button>
              </div>
              <div className="fy-stats">
                <span>📄 Tax Invoices: {data.invoices}</span>
                <span>📦 Purchases: {data.purchases}</span>
                <span>💵 Cash Bills: {data.rawBills}</span>
                <span>💰 Payments: {data.payments}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* CA Export Section */}
      <div className="section-card">
        <h3>📧 Export for CA / Audit</h3>
        <p className="ca-note">⚠️ Cash bills are automatically excluded from CA export as they are unofficial. Only tax invoices and purchase orders are included.</p>
        <button className="ca-export-btn" onClick={() => { loadBillsForExport(); setShowCAExport(true); }}>
          📊 Export Official Bills for CA
        </button>
      </div>
      
      {/* Backup Financial Year Modal */}
      {showBackupFYModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>📅 Backup Financial Year</h3>
              <button className="close-modal" onClick={() => setShowBackupFYModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p>Select a financial year to backup. This will include all transactions for that year along with master data (items, customers, company).</p>
              <select value={selectedFYForBackup} onChange={(e) => setSelectedFYForBackup(e.target.value)} className="fy-select">
                <option value="">Select Financial Year</option>
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowBackupFYModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleBackupFinancialYear} disabled={!selectedFYForBackup || backupFYLoading}>
                {backupFYLoading ? "Backing up..." : "Backup Selected Year"}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* CA Export Modal */}
      {showCAExport && (
        <div className="modal-overlay">
          <div className="modal-content large-modal">
            <div className="modal-header">
              <h3>📧 Export Official Bills for CA</h3>
              <button className="close-modal" onClick={() => { setShowCAExport(false); }}>✕</button>
            </div>
            
            <div className="ca-warning">
              ⚠️ <strong>Note:</strong> Cash bills are automatically excluded from this export. Only Tax Invoices and Purchase Orders are included.
            </div>
            
            <div className="filters-row">
              <div className="filter-group"><label>Search:</label><input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></div>
              <div className="filter-group"><label>Bill Type:</label><select value={filterType} onChange={(e) => setFilterType(e.target.value)}><option value="all">All Official Bills</option><option value="tax-invoice">Tax Invoices</option><option value="purchase">Purchase Orders</option></select></div>
              <div className="filter-group"><label>Financial Year:</label><select value={financialYear} onChange={(e) => setFinancialYear(e.target.value)}><option value="all">All Years</option>{availableYears.map(year => (<option key={year} value={year}>{year}</option>))}</select></div>
              <div className="filter-group"><label>Format:</label><select value={exportFormat} onChange={(e) => setExportFormat(e.target.value)}><option value="excel">Excel Only</option><option value="zip">ZIP (Excel + PDFs)</option></select></div>
              <button className="select-all-btn" onClick={selectAllBills}>{selectedBills.length === filteredBills.length ? "Deselect All" : "Select All"}</button>
            </div>
            
            <div className="bill-list">
              {exportLoading ? <div className="loading-state"><div className="spinner"></div><p>Loading bills...</p></div> :
               filteredBills.length === 0 ? <div className="empty-state"><p>No official bills found for the selected criteria</p></div> :
               filteredBills.map(bill => (
                <div key={bill.id} className="bill-item">
                  <input type="checkbox" checked={selectedBills.some(b => b.id === bill.id)} onChange={() => toggleBillSelection(bill)} />
                  <div className="bill-info">
                    <div className="bill-number"><strong>{bill.invoiceNo || bill.purchaseNo}</strong><span className={`bill-type ${bill.type}`}>{bill.typeLabel}</span></div>
                    <div className="bill-party">{bill.customerName || bill.supplierName}</div>
                    <div className="bill-date">{formatDate(bill.date)}</div>
                    <div className="bill-amount">{formatCurrency(bill.grandTotal || 0)}</div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="modal-footer">
              <div className="selected-count">Selected: {selectedBills.length} official bills</div>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => { setShowCAExport(false); }}>Cancel</button>
                <button className="btn-primary" onClick={handleCAExport} disabled={selectedBills.length === 0 || exportLoading}>
                  {exportLoading ? "Exporting..." : `Export ${selectedBills.length} Official Bills`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Financial Year Modal */}
      {showDeleteFYModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header"><h3>⚠️ Delete Financial Year</h3><button className="close-modal" onClick={() => setShowDeleteFYModal(false)}>✕</button></div>
            <div className="modal-body">
              <p><strong>Warning:</strong> You are about to delete all data for financial year <strong>{selectedFYToDelete}</strong>.</p>
              <p>This includes: All Tax Invoices, Purchase Orders, Cash Bills, Payment Transactions</p>
              <p><strong style={{ color: "#dc3545" }}>This action cannot be undone!</strong></p>
              <p>Company details, items, and customers will NOT be affected.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDeleteFYModal(false)}>Cancel</button>
              <button className="btn-danger" onClick={handleDeleteFinancialYear} disabled={deleteFYLoading}>{deleteFYLoading ? "Deleting..." : `Delete ${selectedFYToDelete}`}</button>
            </div>
          </div>
        </div>
      )}
      
      {/* ================= BOTTOM NAV ================= */}
      <div className="bottom-nav">
        <div onClick={() => navigate("/")} className="nav-item active">
          🏠
          <p>Home</p>
        </div>
        <div onClick={() => navigate("/items")} className="nav-item">
          📦
          <p>Items</p>
        </div>
        <div onClick={() => navigate("/customers")} className="nav-item">
          👥
          <p>Masters</p>
        </div>
        <div onClick={() => navigate("/invoicelist")} className="nav-item">
          🧾
          <p>Invoices</p>
        </div>
      </div>
      
      <style>{`
        .data-management-page { min-height: 100vh; background: #f5f5f5; padding-bottom: 70px; }
        .page-header { display: flex; align-items: center; justify-content: space-between; padding: 15px 20px; background: white; border-bottom: 1px solid #eee; position: sticky; top: 0; z-index: 100; }
        .page-header h2 { margin: 0; font-size: 20px; color: #333; }
        .back-btn { background: #f0f0f0; color: #333; border: none; padding: 8px 16px; border-radius: 20px; font-size: 14px; cursor: pointer; }
        .stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; padding: 0 20px; margin: 20px 0; }
        @media (max-width: 768px) { .stats-grid { grid-template-columns: repeat(2, 1fr); } }
        .stat-card { background: white; border-radius: 12px; padding: 15px; display: flex; align-items: center; gap: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); }
        .stat-icon { font-size: 32px; }
        .stat-info { flex: 1; }
        .stat-label { font-size: 11px; color: #666; }
        .stat-value { font-size: 18px; font-weight: bold; color: #5a60ea; }
        .section-card { background: white; border-radius: 12px; margin: 20px; padding: 20px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); }
        .section-card h3 { margin: 0 0 10px 0; font-size: 18px; }
        .section-card p { color: #666; margin-bottom: 15px; font-size: 13px; }
        .ca-note { background: #fff3e0; padding: 10px; border-radius: 8px; border-left: 3px solid #ff9800; margin-bottom: 15px; }
        .backup-buttons { display: flex; gap: 12px; flex-wrap: wrap; }
        .backup-btn, .backup-fy-btn, .restore-btn, .ca-export-btn { padding: 12px 24px; border: none; border-radius: 8px; font-size: 14px; font-weight: bold; cursor: pointer; transition: all 0.2s; }
        .backup-btn { background: #28a745; color: white; }
        .backup-fy-btn { background: #17a2b8; color: white; }
        .restore-btn { background: #ffc107; color: #333; }
        .ca-export-btn { background: #5a60ea; color: white; }
        .financial-years-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 12px; margin-top: 15px; }
        .financial-year-card { background: #f8f9fa; border-radius: 8px; padding: 12px; border: 1px solid #e0e0e0; }
        .fy-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; padding-bottom: 8px; border-bottom: 1px solid #e0e0e0; }
        .fy-header strong { font-size: 14px; color: #5a60ea; }
        .delete-fy-btn { background: none; border: none; color: #dc3545; font-size: 12px; cursor: pointer; padding: 2px 8px; border-radius: 4px; }
        .fy-stats { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; font-size: 11px; color: #666; }
        .error-alert, .success-alert { margin: 0 20px 20px; padding: 12px 15px; border-radius: 8px; display: flex; align-items: center; justify-content: space-between; }
        .error-alert { background: #ffebee; color: #c62828; border-left: 3px solid #c62828; }
        .success-alert { background: #e8f5e9; color: #2e7d32; border-left: 3px solid #2e7d32; }
        .ca-warning { background: #fff3e0; padding: 12px 20px; margin: 10px 20px 0 20px; border-left: 3px solid #ff9800; font-size: 12px; color: #856404; }
        .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .modal-content { background: white; border-radius: 12px; width: 90%; max-width: 500px; max-height: 85vh; overflow: hidden; display: flex; flex-direction: column; }
        .large-modal { max-width: 900px; }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px; border-bottom: 1px solid #eee; }
        .modal-header h3 { margin: 0; }
        .close-modal { background: none; border: none; font-size: 20px; cursor: pointer; color: #999; }
        .modal-body { padding: 20px; }
        .modal-body select { width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 8px; margin-top: 10px; }
        .filters-row { padding: 15px 20px; background: #f9f9f9; display: flex; flex-wrap: wrap; gap: 10px; align-items: flex-end; border-bottom: 1px solid #eee; }
        .filters-row .filter-group { flex: 1; min-width: 120px; }
        .filters-row .filter-group label { display: block; font-size: 11px; color: #666; margin-bottom: 4px; }
        .filters-row .filter-group input, .filters-row .filter-group select { width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 6px; font-size: 13px; }
        .select-all-btn { padding: 8px 16px; background: #5a60ea; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 12px; }
        .bill-list { flex: 1; overflow-y: auto; padding: 15px; max-height: 400px; }
        .bill-item { display: flex; align-items: center; gap: 15px; padding: 12px; border-bottom: 1px solid #f0f0f0; cursor: pointer; }
        .bill-item:hover { background: #f5f5f5; }
        .bill-item input { width: 18px; height: 18px; cursor: pointer; }
        .bill-info { flex: 1; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; }
        .bill-number { min-width: 120px; }
        .bill-number strong { font-size: 14px; }
        .bill-type { display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 10px; margin-left: 8px; }
        .bill-type.tax-invoice { background: #e3f2fd; color: #1976d2; }
        .bill-type.purchase { background: #fff3e0; color: #f57c00; }
        .bill-party { flex: 1; font-size: 13px; color: #333; }
        .bill-date { font-size: 12px; color: #666; min-width: 90px; }
        .bill-amount { font-size: 14px; font-weight: bold; color: #5a60ea; min-width: 100px; text-align: right; }
        .modal-footer { padding: 15px 20px; border-top: 1px solid #eee; display: flex; justify-content: space-between; align-items: center; }
        .selected-count { font-size: 13px; color: #666; }
        .modal-actions { display: flex; gap: 10px; }
        .btn-secondary, .btn-primary, .btn-danger { padding: 8px 20px; border: none; border-radius: 6px; cursor: pointer; font-size: 13px; }
        .btn-secondary { background: #f0f0f0; color: #333; }
        .btn-primary { background: #5a60ea; color: white; }
        .btn-danger { background: #dc3545; color: white; }
        .loading-state, .empty-state { text-align: center; padding: 40px; color: #666; }
        .spinner { display: inline-block; width: 16px; height: 16px; border: 2px solid #fff; border-top-color: transparent; border-radius: 50%; animation: spin 1s linear infinite; margin-right: 8px; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .bottom-nav { position: fixed; bottom: 0; left: 0; right: 0; background: white; display: flex; justify-content: space-around; padding: 8px 0; box-shadow: 0 -2px 10px rgba(0,0,0,0.05); z-index: 100; }
        .bottom-nav div { text-align: center; cursor: pointer; color: #999; font-size: 20px; }
        .bottom-nav p { margin: 2px 0 0; font-size: 11px; }
      `}</style>
    </div>
  );
}